import { useEffect, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import { relDate } from "../lib/format";
import { WEBHOOK_EVENTS } from "../lib/types";
import type { ApiKey, WebhookEndpoint, WebhookDelivery, WebhookEvent } from "../lib/types";
import * as work from "../lib/workClient";

const EVENT_LABEL: Record<WebhookEvent, string> = {
  "account.health_changed": "Health changed",
  "signal.created": "Signal created",
  "task.created": "Task created",
  "renewal.updated": "Renewal updated",
  "survey.response": "Survey response",
};

export default function Developers() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [keys, setKeys] = useState<ApiKey[]>(ws.apiKeys);
  const [hooks, setHooks] = useState<WebhookEndpoint[]>(ws.webhooks);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>(ws.webhookDeliveries);
  const [reveal, setReveal] = useState<{ title: string; note: string; secret: string } | null>(null);

  useEffect(() => { work.loadDeveloper(ws).then((r) => { setMode(r.mode); setKeys(r.keys); setHooks(r.webhooks); setDeliveries(r.deliveries); }); }, [ws]);

  const newKey = async () => {
    const name = window.prompt("Name this key", "New key")?.trim();
    if (!name) return;
    const { key, secret } = await work.createApiKey(mode, ws, name);
    setKeys((prev) => [key, ...prev]);
    setReveal({ title: "API key created", note: `This is the only time ${key.name}'s full secret is shown.`, secret });
  };
  const revoke = async (k: ApiKey) => {
    if (!window.confirm(`Revoke "${k.name}"? Any integration using it will stop working.`)) return;
    setKeys((prev) => prev.map((x) => (x.id === k.id ? { ...x, revokedAt: new Date().toISOString() } : x)));
    await work.revokeApiKey(mode, ws, k.id);
  };

  const addHook = async (url: string, events: WebhookEvent[]) => {
    const { endpoint, secret } = await work.createWebhook(mode, ws, { url, events });
    setHooks((prev) => [endpoint, ...prev]);
    setReveal({ title: "Webhook created", note: "Use this signing secret to verify the X-Penumbrai-Signature header. Shown once.", secret });
  };
  const toggleHook = async (w: WebhookEndpoint) => {
    setHooks((prev) => prev.map((x) => (x.id === w.id ? { ...x, active: !x.active } : x)));
    await work.updateWebhook(mode, ws, w.id, { active: !w.active });
  };
  const delHook = async (w: WebhookEndpoint) => {
    setHooks((prev) => prev.filter((x) => x.id !== w.id));
    setDeliveries((prev) => prev.filter((d) => d.endpointId !== w.id));
    await work.deleteWebhook(mode, ws, w.id);
  };
  const testHook = async (w: WebhookEndpoint) => {
    const d = await work.testWebhook(mode, ws, w.id);
    if (d) setDeliveries((prev) => [d, ...prev]);
  };

  const activeKeys = keys.filter((k) => !k.revokedAt).length;
  const liveHooks = hooks.filter((w) => w.active).length;

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Developers</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>API &amp; webhooks</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Build on Penumbrai. Issue API keys, subscribe endpoints to workspace events, and watch every delivery.</p>
        </div>
        <ModePill mode={mode} />
      </div>

      <div className="stats">
        <Stat icon="user" label="Active API keys" value={String(activeKeys)} foot={`${keys.length - activeKeys} revoked`} />
        <Stat icon="plug" label="Live webhooks" value={String(liveHooks)} foot={`of ${hooks.length} endpoints`} />
        <Stat icon="pulse" label="Recent deliveries" value={String(deliveries.length)} foot={`${deliveries.filter((d) => d.status === "failed").length} failed`} color={deliveries.some((d) => d.status === "failed") ? "var(--watch)" : undefined} />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card__hd"><div><h3>API keys</h3><span className="sub">Authenticate requests to api.penumbrai.ai</span></div><button className="btn btn--primary btn--sm" onClick={newKey}><Icon name="plus" size={14} /> Create key</button></div>
        <div style={{ overflowX: "auto" }}>
          {keys.length === 0 ? <div className="card__bd"><Empty>No API keys yet.</Empty></div> : (
            <table className="table">
              <thead><tr><th>Name</th><th>Token</th><th>Created</th><th>Last used</th><th></th></tr></thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} style={{ opacity: k.revokedAt ? 0.5 : 1 }}>
                    <td><b style={{ fontSize: 13 }}>{k.name}</b></td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}>{k.prefix}_••••{k.last4}</td>
                    <td className="faint" style={{ fontSize: 12.5 }}>{relDate(k.createdAt)}</td>
                    <td className="faint" style={{ fontSize: 12.5 }}>{k.lastUsedAt ? relDate(k.lastUsedAt) : "—"}</td>
                    <td style={{ textAlign: "right" }}>{k.revokedAt ? <span className="badge badge--neutral">Revoked</span> : <button className="btn btn--ghost btn--sm" onClick={() => revoke(k)}>Revoke</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="auto-layout">
        <div style={{ display: "grid", gap: 16 }}>
          <NewWebhook onAdd={addHook} />
          <div className="card">
            <div className="card__hd"><div><h3>Endpoints</h3><span className="sub">{hooks.length} configured</span></div></div>
            <div className="card__bd" style={{ display: "grid", gap: 10 }}>
              {hooks.length === 0 ? <Empty>No endpoints yet.</Empty> : hooks.map((w) => (
                <div key={w.id} className="hook-row">
                  <div className="row between" style={{ gap: 8 }}>
                    <span style={{ fontFamily: "var(--mono)", fontSize: 12.5, wordBreak: "break-all" }}>{w.url}</span>
                    <label className="toggle" title={w.active ? "Active" : "Paused"}>
                      <input type="checkbox" checked={w.active} onChange={() => toggleHook(w)} /><span />
                    </label>
                  </div>
                  <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                    {w.events.map((e) => <span key={e} className="chip">{EVENT_LABEL[e]}</span>)}
                  </div>
                  <div className="row" style={{ gap: 8, marginTop: 10 }}>
                    <button className="btn btn--sm" onClick={() => testHook(w)}><Icon name="bolt" size={13} /> Send test</button>
                    <button className="btn btn--ghost btn--sm" onClick={() => delHook(w)}>Delete</button>
                    <span className="faint" style={{ fontSize: 11.5, marginLeft: "auto", alignSelf: "center" }}>Signing secret ••••{w.secretLast4}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          <div className="card">
            <div className="card__hd"><div><h3>Delivery log</h3><span className="sub">Most recent first</span></div></div>
            <div style={{ overflowX: "auto" }}>
              {deliveries.length === 0 ? <div className="card__bd"><Empty>No deliveries yet — send a test.</Empty></div> : (
                <table className="table">
                  <thead><tr><th>Event</th><th>Endpoint</th><th>Status</th><th>When</th></tr></thead>
                  <tbody>
                    {deliveries.slice(0, 12).map((d) => {
                      const w = hooks.find((h) => h.id === d.endpointId);
                      return (
                        <tr key={d.id}>
                          <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{d.event}</td>
                          <td className="faint" style={{ fontSize: 12, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w ? new URL(w.url).host : "—"}</td>
                          <td><span className={`badge ${d.status === "success" ? "badge--good" : "badge--risk"}`}>{d.responseCode || "ERR"}</span></td>
                          <td className="faint" style={{ fontSize: 12 }}>{relDate(d.at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card__hd"><div><h3>Quickstart</h3><span className="sub">Every collection is a REST resource</span></div></div>
            <div className="card__bd">
              <pre className="code-block">{`curl https://api.penumbrai.ai/v1/accounts \\
  -H "Authorization: Bearer pk_live_••••"

# Subscribe to events
curl https://api.penumbrai.ai/v1/webhooks \\
  -H "Authorization: Bearer pk_live_••••" \\
  -d url=https://you.example.com/hook \\
  -d events[]=signal.created`}</pre>
            </div>
          </div>
        </div>
      </div>

      {reveal && (
        <div className="cmdk-overlay" onClick={() => setReveal(null)}>
          <div className="compose-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="compose-modal__hd"><div className="row" style={{ gap: 8 }}><Icon name="check" size={16} /><b>{reveal.title} — copy it now</b></div><button className="icon-btn" onClick={() => setReveal(null)}><Icon name="dot" size={16} /></button></div>
            <div className="compose-modal__bd">
              <p className="faint" style={{ fontSize: 13, marginBottom: 10 }}>{reveal.note} Store it somewhere safe.</p>
              <div className="secret-box">{reveal.secret}</div>
            </div>
            <div className="compose-modal__ft">
              <span className="faint" style={{ fontSize: 12 }}>We only keep the last 4 characters.</span>
              <button className="btn btn--primary btn--sm" onClick={() => { navigator.clipboard?.writeText(reveal.secret); setReveal(null); }}>Copy &amp; done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewWebhook({ onAdd }: { onAdd: (url: string, events: WebhookEvent[]) => void }) {
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<WebhookEvent[]>(["signal.created"]);
  const valid = /^https?:\/\/.+/.test(url) && events.length > 0;
  const toggle = (e: WebhookEvent) => setEvents((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));
  const submit = () => { if (valid) { onAdd(url, events); setUrl(""); setEvents(["signal.created"]); } };
  return (
    <div className="card">
      <div className="card__hd"><div><h3>Add webhook</h3><span className="sub">POST events to your endpoint</span></div></div>
      <div className="card__bd" style={{ display: "grid", gap: 12 }}>
        <label className="labeled"><span className="labeled__l">Endpoint URL</span>
          <input className="inp" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://you.example.com/hook" />
        </label>
        <div>
          <span className="labeled__l" style={{ display: "block", marginBottom: 6 }}>Events</span>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {WEBHOOK_EVENTS.map((e) => (
              <button key={e} className={`chip ${events.includes(e) ? "chip--active" : ""}`} style={{ cursor: "pointer" }} onClick={() => toggle(e)}>{EVENT_LABEL[e]}</button>
            ))}
          </div>
        </div>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn--primary btn--sm" disabled={!valid} onClick={submit}>Add endpoint</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, foot, color }: { icon: string; label: string; value: string; foot: string; color?: string }) {
  return (
    <div className="stat">
      <div className="stat__label"><Icon name={icon} size={14} /> {label}</div>
      <div className="stat__value" style={{ color, fontSize: 24 }}>{value}</div>
      <div className="stat__foot">{foot}</div>
    </div>
  );
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}><i />{mode === "api" ? "Live API" : "Local demo"}</span>;
}
